import { isDev, isLocal } from "./env";
import { Quote, QuoteError } from "../types/quote";
import { Amount } from "../types/amount";
import { OrderError } from "../types/order";
import { WarningOrErrorProps } from "../components/WarningOrError/WarningOrError";
import { Chain } from "viem";

export const DEFAULT_ORDER_ERROR_TITLE = "Something went wrong";
export const DEFAULT_QUOTE_ERROR_TITLE = "We couldn't give you a quote";
export const UI_ERRORS = {
  A: {
    title: DEFAULT_QUOTE_ERROR_TITLE,
    body: `Please try again or contact us.`,
  },
  AA: {
    title: DEFAULT_ORDER_ERROR_TITLE,
    body: `Please try again or contact us.`,
  },
  B: {
    title: "Insufficient liquidity",
    body: `There is insufficient liquidity for the trade at this time. Please reduce the trade size.`,
  },
  C: {
    title: "Markets move fast",
    body: `You may have taken too long to confirm the trade in your wallet. Please try again.`,
  },
  D: {
    title: "Insufficient approvals",
    body: `You seem to have set your token approvals on Bebop to custom amounts. Please try again.`,
  },
  E: {
    title: "Price moved too much",
    body: `Unfortunately, the trade could not go ahead at this price. Please try again.`,
  },
} as const;

export const TRADE_SIZE_ERROR_CODE = 104 | 106;
export const QUOTE_ERROR_CODES_MAPPING: Record<number, keyof typeof UI_ERRORS> = {
  101: "A",
  102: "B",
  103: "A",
  // 104 and 106 are special cases
  105: "A",
  108: "A",
  // HTTP - Status codes
  400: "A",
  403: "A",
  404: "A",
  429: "A",
  500: "A",
  501: "A",
  522: "A",
} as const;

export const ORDER_ERROR_CODE_MAPPING: Record<number, keyof typeof UI_ERRORS> = {
  201: "AA",
  202: "E",
  203: "C",
  204: "AA",
  205: "D",
  206: "AA",
  207: "AA",
  208: "AA",
  209: "AA",
  210: "AA",
  211: "AA",
  // HTTP - Status codes
  400: "AA",
  403: "AA",
  404: "AA",
  429: "AA",
  500: "AA",
  501: "AA",
  522: "AA",
} as const;

type QuoteErrorCode = keyof typeof QUOTE_ERROR_CODES_MAPPING;
type OrderErrorCode = keyof typeof ORDER_ERROR_CODE_MAPPING;

const UNEXPECTED_ERROR =
  "There was an unexpected error. Please try again later. If the error persists contact a member of our team";

export const getTradeErrorFromQuote = (
  chain: Chain,
  quoteError?: QuoteError
): WarningOrErrorProps | undefined => {
  if (quoteError?.error) {
    const errorCode = quoteError.error.errorCode;
    if (isValidQuoteErrorCode(errorCode)) {
      // Special case for error code 106 or 104 - the error contains fee in usd and ether.
      if (errorCode === TRADE_SIZE_ERROR_CODE) {
        const messageWithFee = getIncreaseTradeSizeMessage(chain, quoteError.error.fee?.usd);
        if (errorCode === TRADE_SIZE_ERROR_CODE) {
          return {
            type: "error",
            title: "Increase your trade size",
            body: messageWithFee,
          };
        }
      }
      return {
        type: "error",
        title: UI_ERRORS[QUOTE_ERROR_CODES_MAPPING[errorCode]].title,
        body: UI_ERRORS[QUOTE_ERROR_CODES_MAPPING[errorCode]].body,
      };
    } else if (isLocal || isDev) {
      return {
        type: "error",
        title: "DEV ONLY ERROR",
        body: `[${quoteError.error.errorCode}] ${quoteError.error.message}`,
      };
    }
    return {
      type: "error",
      title: DEFAULT_QUOTE_ERROR_TITLE,
      body: UNEXPECTED_ERROR,
    };
  }
  return undefined;
};

export const getErrorFromOrderError = (
  orderError?: OrderError
): WarningOrErrorProps | undefined => {
  if (orderError?.error) {
    const errorCode = orderError.error.errorCode;
    if (isValidOrderError(errorCode)) {
      return {
        type: "error",
        title: UI_ERRORS[QUOTE_ERROR_CODES_MAPPING[errorCode]].title,
        body: UI_ERRORS[QUOTE_ERROR_CODES_MAPPING[errorCode]].body,
      };
    } else if (isLocal || isDev) {
      return {
        type: "error",
        title: "DEV ONLY ERROR",
        body: `[${orderError.error.errorCode}] ${orderError.error.message}`,
      };
    } else {
      return {
        type: "error",
        title: DEFAULT_ORDER_ERROR_TITLE,
        body: UNEXPECTED_ERROR,
      };
    }
  }
  return undefined;
};

export const getQuoteWarnings = (
  quote: Quote | undefined,
  chain: Chain
): WarningOrErrorProps | undefined => {
  if (!quote) {
    return undefined;
  }
  const networkFee = quote.usdGasFee;
  const totalAmountOut = Object.values(quote.takerTokens)
    .map(entry => entry.amountUsd)
    .reduce((acc, currentValue) => acc.add(currentValue), new Amount("0"));
  const gasPercentageOfTradeAmount = networkFee.value.div(totalAmountOut.value).mul(100);
  if (gasPercentageOfTradeAmount.gt(10)) {
    const warningMessage = `The current ${chain.name} cost is ${gasPercentageOfTradeAmount
      .round()
      .toString()}% of this trade.`;
    return {
      type: "warning",
      title: "Consider increasing your trade size",
      body: warningMessage,
    };
  }
  return undefined;
};

const isValidQuoteErrorCode = (maybeErrorCode: number): maybeErrorCode is QuoteErrorCode => {
  const allErrorCodes = Object.keys(QUOTE_ERROR_CODES_MAPPING).map(Number);
  allErrorCodes.push(TRADE_SIZE_ERROR_CODE);
  return allErrorCodes.includes(maybeErrorCode);
};
const isValidOrderError = (maybeErrorCode: number): maybeErrorCode is OrderErrorCode => {
  return Object.keys(ORDER_ERROR_CODE_MAPPING).includes(maybeErrorCode.toString());
};

const getIncreaseTradeSizeMessage = (chain: Chain, fee: number | undefined): string => {
  if (fee) {
    return `The current ${chain.name} network cost is $${fee}. Please increase the trade size to cover this amount.`;
  }
  return `The current ${chain.name} cost is not covered by your trade. Please increase the trade size.`;
};
